import { FormEvent, useEffect, useState } from 'react';
import { Form, Modal } from 'react-bootstrap';
import { format, parseISO } from 'date-fns';
import { MdCallSplit, MdOutlineSchedule } from 'react-icons/md';
import { useAppSelector } from '@src/redux';
import { currentCompanySelector, initialKpiSelector } from '@src/redux/company';
import { useSetError } from '@src/redux/modals';
import { useCompanyInfo } from '@src/utils/hooks/useCompanyInfo';
import { useTabbedNav } from '@src/utils/hooks/useNav';
import { Button } from '@Components/Button';
import { Appearance, Design, Size } from '@Components/designConstants';
import { DownloadDropdown } from '@Components/DownloadDropdown';
import { KpiPicker } from '@Components/KpiPicker';
import { Page } from '@Components/Page';
import { DEFAULT_BRANCH, NAVS, TabKey, TabKeyType } from './linear-ml-diagnostics-constants';
import {
  downloadKpiDebug,
  downloadStageOneCsv,
  useStageTwoDiagnostics
} from './use-linear-ml-diagnostics';
import { StageOneDiagnostics } from './StageOneDiagnostics';
import { StageTwoDiagnostics } from './StageTwoDiagnostics';
import './LinearMLDiagnostics.scss';

const DATE_FORMAT = 'MMM d, yyyy h:mm a';

const isTabKey = (value: string): value is TabKeyType => NAVS.some(({ key }) => key === value);

interface BranchPickerModalProps {
  branch: string;
  show: boolean;
  onHide: () => void;
  onSubmit: (branch: string) => void;
}

const BranchPickerModal = ({ branch, show, onHide, onSubmit }: BranchPickerModalProps) => {
  const [modalBranch, setModalBranch] = useState(branch);
  const trimmed = modalBranch.trim();
  const submit = (e: FormEvent) => {
    e.preventDefault();
    onSubmit(trimmed);
  };
  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      dialogClassName='linear-ml-branch-modal'
      data-testid='branch-modal'
    >
      <Form onSubmit={submit}>
        <Modal.Header closeButton>
          <Modal.Title>Set model branch</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group controlId='linear-ml-branch-name'>
            <Form.Label>Branch name</Form.Label>
            <Form.Control
              autoFocus
              data-testid='branch-input'
              value={modalBranch}
              placeholder={DEFAULT_BRANCH}
              onChange={e => setModalBranch(e.currentTarget.value)}
            />
            <Form.Text>
              Reads ML output from bpm-ml-data/&lt;branch&gt;/&lt;kpi&gt;/latest.
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button appearance={Appearance.OUTLINED} design={Design.NEUTRAL} onClick={onHide}>
            Cancel
          </Button>
          <Button type='submit' disabled={!trimmed || trimmed === branch}>
            Update branch
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export const LinearMLDiagnostics = () => {
  const setError = useSetError();
  const company = useAppSelector(currentCompanySelector);
  const initialKpi = useAppSelector(initialKpiSelector(company));
  const companyInfo = useCompanyInfo();
  const [kpi, setKpi] = useState(initialKpi);
  const [branch, setBranch] = useState(DEFAULT_BRANCH);
  const [showBranchPickerModal, setShowBranchPickerModal] = useState(false);
  const [fetchingCSV, setFetchingCSV] = useState(false);
  const [downloadingKpiDebug, setDownloadingKpiDebug] = useState(false);

  const { tab, goToTab } = useTabbedNav({
    baseURL: 'linear/diagnostics',
    defaultKey: TabKey.STAGE_ONE,
    navs: NAVS.map(nav => nav.key)
  });

  const stageTwo = useStageTwoDiagnostics(kpi, branch, tab === TabKey.STAGE_TWO);
  const timestamp = tab === TabKey.STAGE_TWO ? stageTwo.data?.stageTwoFileTimeStamp : undefined;

  useEffect(() => {
    if (stageTwo.error) {
      setError({ message: stageTwo.error.message, reportError: stageTwo.error });
    }
  }, [stageTwo.error, setError]);

  const onDownloadCSV = async () => {
    if (!kpi) return;
    setFetchingCSV(true);
    try {
      await downloadStageOneCsv(kpi, branch);
    } catch (e) {
      setError({ message: 'Could not find the file for the KPI.', reportError: e });
    }
    setFetchingCSV(false);
  };

  const onDownloadKpiDebug = async () => {
    if (!kpi) return;
    setDownloadingKpiDebug(true);
    try {
      await downloadKpiDebug(kpi);
    } catch (e) {
      setError({ message: `Could not download KPI Debug. Error: ${e.message}`, reportError: e });
    }
    setDownloadingKpiDebug(false);
  };

  return (
    <Page
      title='Linear ML Diagnostics'
      pageType='Linear ML Diagnostics'
      className='linear-ml-diagnostics'
      minWidth='800px'
      navs={NAVS}
      selectedNav={tab}
      onNav={nav => {
        if (isTabKey(nav)) {
          goToTab(nav);
        }
      }}
      actions={
        <div className='ml-diagnostics-actions'>
          {timestamp && (
            <div className='last-completion-date' data-testid='last-completion-date'>
              <MdOutlineSchedule aria-hidden />
              {`Last completed ${format(parseISO(timestamp), DATE_FORMAT)}`}
            </div>
          )}
          <Button
            design={Design.NEUTRAL}
            leadingElement={<MdCallSplit />}
            tooltip={{ text: 'Model branch' }}
            data-testid='branch-button'
            onClick={() => setShowBranchPickerModal(true)}
          >
            {`Branch: ${branch}`}
          </Button>
          <KpiPicker
            size={Size.MD}
            disableTypeSelector
            noBreak
            onChange={setKpi}
            defaultKpi={companyInfo.optimization_cid}
          />
          <DownloadDropdown
            onClickOptions={[onDownloadCSV, onDownloadKpiDebug]}
            menuOptions={['CSV', 'KPI Debug']}
            disabled={fetchingCSV || downloadingKpiDebug}
          />
          <BranchPickerModal
            key={branch}
            show={showBranchPickerModal}
            branch={branch}
            onHide={() => setShowBranchPickerModal(false)}
            onSubmit={newBranch => {
              setShowBranchPickerModal(false);
              setBranch(newBranch);
            }}
          />
        </div>
      }
    >
      {tab === TabKey.STAGE_TWO ? (
        <StageTwoDiagnostics kpi={kpi} branch={branch} />
      ) : (
        <div className='ml-diagnostics'>
          <StageOneDiagnostics kpi={kpi} branch={branch} />
        </div>
      )}
    </Page>
  );
};
